import {
  IconFolderTile,
  IconLayers,
  IconStandardBox,
  LockOutlined,
} from '../../../components/icons'
import { AvatarStack } from '../../../components/ui/Avatar'
import { formatDate } from '../format'
import type { WorkspaceSummary } from '../types'
import { tintClassForWorkspace } from '../workspace-tint'

export interface WorkspaceCardProps {
  readonly workspace: WorkspaceSummary
  readonly onOpen: (id: string) => void
}

/**
 * All-workspaces card (screens.md §9: "colored letter avatar, member
 * count, contents summary, lock icon if private+not-a-member"). A real
 * `<button>` (not a `<div role="button">`) so click/Enter/Space and focus
 * come for free. Per the frozen `GET /api/workspaces` contract (types.ts),
 * a private workspace the caller isn't a member of is never returned by
 * the list at all — so `myRole === null` on a `visibility: 'private'` row
 * shouldn't occur today, but the lock affordance is kept (defensive,
 * matches the spec's stated card state) in case that ever changes
 * server-side.
 */
export function WorkspaceCard({ workspace, onOpen }: WorkspaceCardProps) {
  const isLockedPrivate = workspace.visibility === 'private' && workspace.myRole === null
  // UID-393 — `null` (no description set) and a whitespace-only string collapse
  // to the same "render no line at all" case; see the desc block below.
  const description = workspace.description?.trim() ?? ''

  return (
    <button
      type="button"
      // UID-391 a11y fix — the private state is folded into the button's OWN
      // accessible name rather than carried on the lock glyph. An `aria-label`
      // on a button overrides ALL descendant content for name computation, so
      // the `aria-label` this icon used to carry was never announced: the state
      // was visible-only and silently dropped for screen-reader users.
      // `WorkspaceListRow.tsx:76-88` already fixed this for the list view and
      // its comment flagged this component as carrying the same latent gap;
      // this is that fix, so the two views now announce identically.
      aria-label={
        isLockedPrivate
          ? `Open ${workspace.name} (private — you are not a member)`
          : `Open ${workspace.name}`
      }
      className="card card-interactive workspace-card"
      onClick={() => onOpen(workspace.id)}
    >
      {/* UID-391 gap 1+2 — the leading icon and the name sit SIDE-BY-SIDE in one
       * flex row, per the comp's grid card (`Fictiv Platform.dc.html:1623-1628`
       * on the All-projects page; the home-screen `projectCards` instance at
       * `:1570-1575` is byte-identical markup, and `:8768` derives `wsAllCards`
       * from `projectCards` by a `.filter`, so the two can never disagree).
       * They were previously stacked: the icon owned a full-width
       * `space-between` row and the name was a separate `<p>` below it.
       *
       * The comp nests THREE levels here — the row, a `flex:1;min-width:0`
       * name/desc block, and inside that an `align-items:center;gap:7px` row
       * holding the name and the lock. That middle level exists ONLY to stack
       * the name above `{{ p.desc }}`.
       *
       * ~~`WorkspaceSummary` carries no description field, so the middle level
       * would be a wrapper with a single child and is collapsed into
       * `.workspace-card-name`.~~ **RESOLVED by UID-393** — `description` is
       * now ON the summary contract (`openapi.yaml`/`types.ts`), so the middle
       * level is BACK, exactly as the struck-through note above instructed
       * ("if `description` is ever added to the summary contract, re-introduce
       * the middle level rather than trying to squeeze a second line into this
       * flex row"). It has to be: the name row is `align-items:center`, so a
       * second line could not stack inside it.
       *
       * The split is `.workspace-card-name-block` (the comp's middle level:
       * `flex:1;min-width:0`, stacking name over desc) wrapping
       * `.workspace-card-name` (the inner `align-items:center;gap:7px` row).
       * `flex:1;min-width:0` MOVED off `.workspace-card-name` onto the new
       * block, so a card with NO description renders pixel-identically to
       * before this change.
       *
       * The LOCK moves with the name (comp `:1626`: immediately after the name
       * text at a 7px gap, `flex-shrink:0`) instead of being pushed to the far
       * right of the card by the old row's `justify-content: space-between`.
       * Its `isLockedPrivate` condition is deliberately UNCHANGED — see the
       * component doc comment above. Note the comp's own lock is driven by
       * `locked: !joined` (`:8764`), i.e. membership alone, because the comp's
       * data model has no `visibility` axis; this repo's is narrower on
       * purpose and that call is not UID-391's to revisit.
       *
       * UID-392 made good on that: the leading glyph is now the comp's tinted
       * folder swatch, swapped in place without touching which row it sits in. */}
      <div className="workspace-card-header">
        {/* UID-392 — comp `:1571`/`:1624` (byte-identical blocks): a 44px
         * SQUARE `border-radius:4px` swatch tinted from the project's own
         * colour, holding a 23px folder glyph. This replaces the round,
         * name-hashed `<Avatar size={40}>` that stood in before, which was
         * wrong on all three axes the comp specifies — shape (round vs
         * square), colour seed (`name` vs `id`) and glyph (person-style
         * initials vs the folder).
         *
         * `tintClassForWorkspace` is the SHARED util (`../workspace-tint`),
         * not a local copy: the sidebar's Projects mini-list already tinted
         * the same projects by the same rule, and the whole point of this
         * ticket is that one project reads as ONE colour across the sidebar,
         * this grid card and the list row. Two copies of the hash would drift.
         * Seeded on the immutable `id`, so renaming a project never recolors
         * it — see the util's comment for why not `name` and why not
         * index-cycled.
         *
         * `aria-hidden` on the wrapper: the glyph is decoration beside the
         * card's own visible name text (and the button's accessible name
         * already carries every state the card conveys), so WCAG 1.4.11's
         * decorative exemption applies — which is what lets the comp's exact
         * hues ship despite tint-3 measuring only 2.45:1. Same posture as
         * `Sidebar.tsx`'s expanded row. Do NOT reuse this palette on real
         * text without an override (cf. `.sidebar-workspaces-collapsed-letter`
         * in `shell.css`). */}
        <span
          className={`workspace-card-icon ${tintClassForWorkspace(workspace.id)}`}
          aria-hidden="true"
        >
          <IconFolderTile size={23} />
        </span>
        <div className="workspace-card-name-block">
          <p className="workspace-card-name">
            {workspace.name}
            {/* Decorative — the state it conveys is in the button's accessible
             * name above, so announcing it here too would be a duplicate (and an
             * `aria-label` here would not be announced at all). `title` is kept
             * for the mouse-hover tooltip. Matches `WorkspaceListRow.tsx:102-108`
             * exactly. */}
            {isLockedPrivate && (
              <LockOutlined
                aria-hidden="true"
                title="Private project"
                className="workspace-card-lock"
              />
            )}
          </p>
          {/* UID-393 — the comp's description line (`:1574`, and the identical
           * all-projects block at `:1627`): 13px `--fg-2`, 2px above the name,
           * line-height 1.35, and NO truncation on the grid card (unlike the
           * list row, which the comp DOES clip to one line).
           *
           * Rendered ONLY for a non-null, non-blank description — an absent one
           * renders NOTHING, not a placeholder and not an empty node. Same
           * presence discipline as `bomCount`/`partCount` above, but expressed
           * as a null/blank check rather than `!== undefined`, because
           * `description` is a REQUIRED NULLABLE field, not an optional one
           * (`types.ts`): the BFF always sends the key, and `null` is how it
           * says "no description set". A blank-but-present string is treated as
           * absent too — it would otherwise reserve vertical space for nothing. */}
          {description !== '' && <p className="workspace-card-desc">{description}</p>}
        </div>
      </div>
      {/* UID-348 — contents summary, above the member affordance to match the
       * comp's card order (`Fictiv Platform.dc.html:1568-1583`: name → desc →
       * counts → members). BOMs precede parts, and the comp uses NO separator
       * between the two chips — the `·`-joined form there belongs to the
       * library filter banner (`:8777`), not this card.
       *
       * Each count is gated on its OWN presence, and an ABSENT count renders
       * NOTHING rather than a zero: per ADR-0114 D2/D6/D7 the BFF omits these
       * fields for a non-member, on the embedded project-chip projections, and
       * when the derivation failed — so `0` would assert "this project is
       * empty" on evidence that says no such thing. An explicit `0` (an empty
       * project the caller belongs to) DOES render. Hence `!== undefined`,
       * never a falsy check and never `?? 0`.
       *
       * Plural nouns are hard-coded, so a single BOM reads "1 BOMs": that is
       * what the comp renders (`:1578`) and ADR-0114 assigns the call to this
       * ticket — human product decision 2026-08-27 to match the comp. */}
      {(workspace.bomCount !== undefined || workspace.partCount !== undefined) && (
        <p className="workspace-card-meta">
          {workspace.bomCount !== undefined && (
            <span className="workspace-card-meta-item">
              <IconStandardBox size={15} />
              {workspace.bomCount} BOMs
            </span>
          )}
          {workspace.partCount !== undefined && (
            <span className="workspace-card-meta-item">
              <IconLayers size={15} />
              {workspace.partCount} parts
            </span>
          )}
          {/* UID-391 gap 4 — the counts row closes with the right-aligned
           * updated date (comp `:1633-1634`: a `flex:1` spacer span, then a
           * 12px `--fg-3` date). The spacer is expressed as `margin-left:auto`
           * on the date itself instead of an empty `<span style="flex:1">` —
           * pixel-identical, one less contentless DOM node.
           *
           * `formatDate` is the workspaces vertical's own pinned-`'en-US'`
           * helper (`../format`), already doing this exact `updatedAt` job for
           * the list view (`WorkspaceListRow.tsx:154`). NOT
           * `verticals/parts/format.ts`'s same-named export, which formats on
           * the runtime default locale — a known, deliberately-unfixed
           * inconsistency this card must not spread.
           *
           * The format MATCHES the comp — no divergence. The comp defines
           * `dcFmt` TWICE, and only one is in scope here: `:8601`
           * (`month:'short'` → "Aug 10, 2026") is declared inside
           * `renderVals()` (opens `:8428`), which is the scope `projectCards`
           * is built in (`:8763`). The other, `:6375` (`month:'long'`), is
           * local to `_buildPartDetail()` and never reaches this card. So
           * `formatDate`'s `'en-US'`/`month:'short'` output is byte-identical
           * to the comp's, and also matches the list view for the same field.
           *
           * This date lives INSIDE the counts-presence gate, so a card whose
           * counts were omitted (ADR-0114 D2/D6/D7) renders no date either.
           * That is intentional scope discipline, not an oversight: hoisting
           * the row out of the gate to carry an always-present `updatedAt`
           * would make `.workspace-card-meta` render for uncounted projects,
           * which two existing tests pin as absent (this file's "renders NO
           * summary at all" case and `AllWorkspacesPage.test.tsx:122`'s
           * cross-view wiring assertion). Flagged for product: if the date
           * should survive a countless card, that is a follow-on ticket that
           * owns re-gating the row and updating both tests. */}
          <span className="workspace-card-updated">{formatDate(workspace.updatedAt)}</span>
        </p>
      )}
      {/* UID-375 — grid-card footer avatar stack (comp `Fictiv Platform.dc.html`
       * `wsAllCards` block, ~:1638): overlapping 28px avatars with a white ring,
       * NO member-count text and NO role text in that footer. `membersPreview`
       * is the UID-374 capped-at-3 preview; `memberCount` (always present,
       * required `number`) feeds `AvatarStack`'s `totalCount` so the `+N`
       * overflow badge reflects the TRUE total rather than `membersPreview
       * .length` (which can never exceed 3).
       *
       * ROLE-CAPTION DECISION (product decision, 2026-08-28, superseding an
       * earlier narrower version of this comment): the `· {myRole}` suffix
       * is dropped from BOTH branches below, not just the avatar-stack one —
       * the comp renders no role text in this footer under ANY state, so
       * neither does this card. The stack's accessible "N members" group
       * label carries the membership signal on the stack path; the
       * fallback's own "N members" text carries it on the fallback path.
       * This is close to a no-op on the fallback: that branch renders when
       * the caller is (almost always) a non-member, where `myRole === null`
       * already produced no suffix — the ONLY state where the suffix used to
       * render on the fallback path was the ADR-0114 D7 degraded read
       * (caller IS a member, but the preview derivation failed), so this
       * removes a string that was already unreachable in ~all real traffic.
       *
       * Branches on presence (`membersPreview !== undefined` and non-empty),
       * never `?? []` — per ADR-0114 D2/D6/D7, absence is not "no members".
       * `membersPreview` ABSENT is a non-comp state (BFF omitted it — caller
       * not an active member, an embedded chip projection, or a failed
       * derivation) and falls back to the member-count text, now without the
       * role suffix either. */}
      {/* UID-391 gap 3 — the footer is a `space-between` row (comp `:1636-1641`)
       * pairing the member affordance on the left with the open label on the
       * right. Both prior footer branches keep their exact markup and move
       * INSIDE this wrapper, so the `space-between` works identically whether
       * the left slot is the avatar stack or the member-count fallback. */}
      <div className="workspace-card-footer">
        {workspace.membersPreview !== undefined && workspace.membersPreview.length > 0 ? (
          <AvatarStack
            people={workspace.membersPreview.map((m) => ({
              id: m.userId,
              name: m.displayName,
              avatarUrl: m.avatarUrl,
            }))}
            max={3}
            size={28}
            totalCount={workspace.memberCount}
          />
        ) : (
          <p className="caption-text">
            {workspace.memberCount} member{workspace.memberCount === 1 ? '' : 's'}
          </p>
        )}
        {/* The label is plain text, NOT a nested control: the whole card is
         * already one `<button>`, and the comp's own `<span>` carries no
         * handler either (`p.onOpen` sits on the card, `:1622`). Nesting an
         * anchor or button here would break that single-activatable-target
         * invariant an existing test pins. The card's `aria-label` supplies the
         * accessible name, so this text is not separately announced and the
         * "→" needs no `aria-hidden`.
         *
         * DELIBERATE DIVERGENCE (data, not styling): the comp's label is
         * conditional — `openLabel: joined ? 'Open →' : 'Request access →'`
         * (`:8764`). `WorkspaceSummary` cannot express that branch:
         * `canRequestAccess` exists only on the FULL `Workspace` and
         * `WorkspaceRedactedStub` (`types.ts:113,137`), never on this list
         * projection. It is also unreachable in practice — the frozen list
         * contract omits private non-member rows entirely, and a PUBLIC
         * non-member row is openable, so "Request access" would be wrong for
         * every row this surface can actually receive. The label is therefore
         * unconditional, matching the card's one real action. */}
        <span className="workspace-card-open">Open →</span>
      </div>
    </button>
  )
}
